import {
  JsonProperty,
  MappingService,
} from '../../core/services/utils/mapping.service';
import {
  AvailabilityEnum,
  DepositType,
  ZeroPriceType,
} from '../../core/types/enums';
import { CartProduct } from './cart.model';
import { Category, CategoryFilter } from './category.model';
import { Entity } from './entity.model';

export class Image {
  @JsonProperty('src')
  src: string = '';
  @JsonProperty()
  productId: number = 0;
  @JsonProperty('origin')
  origin: string = '';
  @JsonProperty('id')
  id: number = 0;
}
export class Availability {
  @JsonProperty('endDate')
  endDate?: string = '';
  @JsonProperty()
  lastModifiedTimestamp: string = '';
  @JsonProperty('status')
  status: string = '';
}
export class ProductCategory {
  @JsonProperty('name')
  name: string = '';
  @JsonProperty()
  categoryId: number = 0;
  @JsonProperty()
  categorySort: number = 0;
  @JsonProperty('iconUrl')
  iconUrl: string = '';
}
export class Brand {
  @JsonProperty()
  brandName: string = '';
}

export class CommonSpecification {
  @JsonProperty('name')
  name: string = '';
  @JsonProperty('id')
  id: string = '';
  @JsonProperty()
  imageUrl: string = '';
}

/**
 * One promotional area of a 0 EUR article. `active` releases it for ordering in that area,
 * `limit` caps the quantity per cart (`null` = unlimited). Both areas replace the single
 * `zeroPriceType` string, which the API still sends until CORE serves the new fields.
 */
export class ZeroPriceSetting {
  @JsonProperty('active')
  active: boolean = undefined;
  @JsonProperty('limit')
  limit: number = null;
}

export class Product extends Entity {
  @JsonProperty('sort')
  sort: number = 0;
  @JsonProperty('publish')
  publish: number = 0;
  @JsonProperty({ clazz: Image, name: 'images' })
  images: Array<Image> = [];
  @JsonProperty('inhalt')
  inhalt: string = '';
  @JsonProperty('price')
  price: number = 0;
  @JsonProperty()
  depositType: DepositType = DepositType.NONE;
  @JsonProperty()
  zeroPriceType: ZeroPriceType = ZeroPriceType.None;
  @JsonProperty({ clazz: ZeroPriceSetting })
  zeroPriceB2B: ZeroPriceSetting = null;
  @JsonProperty({ clazz: ZeroPriceSetting })
  zeroPriceB2C: ZeroPriceSetting = null;
  @JsonProperty({ clazz: Brand, name: 'brand' })
  brand: Brand = null;
  @JsonProperty()
  customerProductId: number = 0;
  @JsonProperty({ clazz: ProductCategory, name: 'categories' })
  categories: Array<ProductCategory> = [];
  @JsonProperty('isNew')
  isNew: boolean = false;
  @JsonProperty('popularity')
  popularity: number = 0;
  @JsonProperty({ clazz: Availability, name: 'availability' })
  availability: Availability = null;
  @JsonProperty()
  alternativeProductId: number | null = null;
  @JsonProperty()
  createDate: string = '';
  quantity: number = 0;
  @JsonProperty('description')
  description: string = '';
  @JsonProperty('title')
  title: string = '';
  @JsonProperty('orderDate')
  orderDates?: string[] = [];
  @JsonProperty()
  categoriesFilter: CategoryFilter = null;
  @JsonProperty('ingredients')
  ingredients: string = '';
  @JsonProperty({ clazz: CommonSpecification })
  commonSpecs: Array<CommonSpecification> = [];

  constructor(init?: Partial<Product>) {
    super();
    Object.assign(this, init);
  }

  convertToCartProduct() {
    return MappingService.deserialize(CartProduct, {
      id: this.id,
      name: this.name,
      quantity: this.quantity,
      price: this.price,
      total: this.price * this.quantity,
      category: this.categories[0]?.name,
      categories: this.categories,
      brand: this.brand?.brandName,
      customerProductId: this.customerProductId,
      sort: this.sort === null ? 1000 : this.sort,
      categoryId: this.categories[0]?.categoryId,
      inSortiment: 1,
      imageUrl: this.images[0].src,
      depositType: this.depositType,
    });
  }

  isUnavailable() {
    if (!this.availability) return true;
    return (
      this.availability.status === AvailabilityEnum.No_permanently ||
      this.availability.status === AvailabilityEnum.No_seasonal ||
      this.availability.status === AvailabilityEnum.No_temporarily
    );
  }
}

export class DetailedProduct extends Product {
  @JsonProperty('favouriteResidentsIds')
  favouriteResidentsIds: number[] = [];
}

export class LastOrderedProducts {
  @JsonProperty({ clazz: Product, name: 'products' })
  products: Array<Product> = [];
  @JsonProperty('orderDate')
  orderDate: string = '';
}

export class GetProductsResponse {
  @JsonProperty({ clazz: Product, name: 'newProducts' })
  newProducts?: Array<Product> = [];
  @JsonProperty({ clazz: Product, name: 'productsByCategory' })
  productsByCategory?: Array<Product> = [];
  @JsonProperty({ clazz: Product, name: 'searchProducts' })
  searchProducts?: Array<Product> = [];
  @JsonProperty({ clazz: Product, name: 'additionalProducts' })
  additionalProducts?: Array<Product> = [];
}
